import { Helmet } from "react-helmet-async";
import Layout from "@/components/layout/Layout";
import { Card } from "@/components/ui/card";

const TermsAndConditions = () => {
  return (
    <Layout>
      <Helmet>
        <title>Terms & Conditions — Datalevo Tools | Walking Weight Loss Calculator</title>
        <meta
          name="description"
          content="Read the terms and conditions for using Datalevo's Walking Weight Loss Calculator and related services."
        />
        <link rel="canonical" href="https://walkingweightlosscalculator.datalevo.com/terms-and-conditions" />
      </Helmet>

      <div className="container mx-auto px-4 py-12 md:py-16">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Terms & Conditions
          </h1>
          <p className="text-muted-foreground mb-8">
            Last updated: December 4, 2025
          </p>

          <Card className="p-6 md:p-8">
            <div className="prose prose-sm md:prose-base max-w-none text-foreground">
              <section className="mb-8">
                <h2 className="text-xl font-semibold text-foreground mb-4">1. Agreement to Terms</h2>
                <p className="text-muted-foreground leading-relaxed">
                  By accessing or using the Walking Weight Loss Calculator and any services provided by Datalevo ("Company," "we," "us," or "our") through our website at walkingweightlosscalculator.datalevo.com, you agree to be bound by these Terms and Conditions ("Terms"). If you disagree with any part of these terms, you may not access or use our services.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-foreground mb-4">2. Service Description</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  The Walking Weight Loss Calculator is a free, privacy-first web-based tool designed to help users estimate calories burned during walking activities and project potential weight changes over time. Our service provides:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2">
                  <li>Calorie burn calculations based on user-provided inputs</li>
                  <li>Weight change projections using validated scientific formulas</li>
                  <li>Personalized walking plans with exportable reports</li>
                  <li>Optional sharing features for results</li>
                </ul>
                <p className="text-muted-foreground leading-relaxed mt-4">
                  All calculations are performed locally in your browser unless you explicitly choose to use server-based features like shareable links.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-foreground mb-4">3. Acceptable Use</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  You agree to use our services only for lawful purposes and in accordance with these Terms. You agree NOT to:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2">
                  <li>Use our services for any illegal purpose or in violation of any applicable laws</li>
                  <li>Attempt to gain unauthorized access to our systems or servers</li>
                  <li>Interfere with or disrupt the integrity or performance of our services</li>
                  <li>Transmit any malicious code, viruses, or harmful programs</li>
                  <li>Scrape, harvest, or collect user data without authorization</li>
                  <li>Use automated systems or bots to access our services excessively</li>
                  <li>Reproduce, duplicate, copy, sell, or resell our services without permission</li>
                  <li>Impersonate any person or entity or misrepresent your affiliation</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-foreground mb-4">4. Intellectual Property Rights</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  The Walking Weight Loss Calculator, including its design, functionality, content, logos, and trademarks, are the exclusive property of Datalevo and are protected by international copyright, trademark, and other intellectual property laws.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  You are granted a limited, non-exclusive, non-transferable license to access and use our services for personal, non-commercial purposes. This license does not include the right to modify, distribute, or create derivative works based on our services without our written consent.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-foreground mb-4">5. Medical Disclaimer</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  <strong className="text-foreground">Important:</strong> The Walking Weight Loss Calculator is provided for informational and educational purposes only. It is NOT a substitute for professional medical advice, diagnosis, or treatment.
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2">
                  <li>Results are estimates based on population averages and validated formulas</li>
                  <li>Individual results may vary significantly due to metabolic differences</li>
                  <li>Always consult a healthcare professional before starting any weight loss or exercise program</li>
                  <li>Do not rely solely on calculator results for health decisions</li>
                  <li>Seek immediate medical attention for any health emergencies</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-foreground mb-4">6. Limitation of Liability</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  To the maximum extent permitted by applicable law, Datalevo and its officers, directors, employees, and agents shall not be liable for:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2">
                  <li>Any indirect, incidental, special, consequential, or punitive damages</li>
                  <li>Any loss of profits, data, use, goodwill, or other intangible losses</li>
                  <li>Any damages resulting from your use or inability to use our services</li>
                  <li>Any errors or inaccuracies in calculations or content</li>
                  <li>Any health outcomes or decisions based on calculator results</li>
                </ul>
                <p className="text-muted-foreground leading-relaxed mt-4">
                  Our services are provided on an "as is" and "as available" basis without warranties of any kind, either express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, or non-infringement.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-foreground mb-4">7. Data & Privacy</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Your use of our services is also governed by our Privacy Policy, which explains how we collect, use, and protect your information. By using our services, you consent to the collection and use of information as described in our Privacy Policy. Key points:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2 mt-4">
                  <li>Calculations are performed client-side (in your browser) by default</li>
                  <li>Server storage only occurs when you create shareable links</li>
                  <li>Shareable links expire after 7 days and data is automatically deleted</li>
                  <li>We do not sell or share your personal health data</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-foreground mb-4">8. Export & Sharing Disclaimers</h2>
                <p className="text-muted-foreground leading-relaxed">
                  When you export your results (PDF, CSV) or create shareable links:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2 mt-4">
                  <li>You are responsible for how exported data is used or distributed</li>
                  <li>Shared links are accessible to anyone with the URL</li>
                  <li>We cannot control how third parties may use shared information</li>
                  <li>Exercise caution when sharing health-related data</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-foreground mb-4">9. Modifications to Service</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We reserve the right to modify, suspend, or discontinue any part of our services at any time without prior notice. We may also update these Terms from time to time. Continued use of our services after changes are posted constitutes acceptance of the revised Terms.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-foreground mb-4">10. Governing Law</h2>
                <p className="text-muted-foreground leading-relaxed">
                  These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in which Datalevo operates, without regard to its conflict of law provisions. Any disputes arising under these Terms shall be resolved through good-faith negotiation or, if necessary, through binding arbitration.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-foreground mb-4">11. Severability</h2>
                <p className="text-muted-foreground leading-relaxed">
                  If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary, and the remaining provisions shall remain in full force and effect.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mb-4">12. Contact Information</h2>
                <p className="text-muted-foreground leading-relaxed">
                  For questions about these Terms & Conditions, please contact us at:
                </p>
                <p className="text-muted-foreground mt-2">
                  <strong className="text-foreground">Email:</strong>{" "}
                  <a href="mailto:contact@datalevo.com" className="text-primary hover:underline">
                    contact@datalevo.com
                  </a>
                </p>
                <p className="text-muted-foreground mt-2">
                  <strong className="text-foreground">Website:</strong>{" "}
                  <a href="https://datalevo.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                    https://datalevo.com
                  </a>
                </p>
              </section>
            </div>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default TermsAndConditions;
